import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Login.css';
import { userService } from '../services';
import ThemeToggle from './ThemeToggle';

const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.6.68 2.35a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.73-1.25a2 2 0 0 1 2.11-.45c.75.32 1.54.55 2.35.68A2 2 0 0 1 22 16.92z" />
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.8 21.8 0 0 1 5.06-6.94" />
    <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a21.77 21.77 0 0 1-2.16 3.19" />
    <path d="M1 1l22 22" />
    <path d="M14.12 14.12A3 3 0 0 1 9.88 9.88" />
  </svg>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const BriefcaseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="7" width="20" height="14" rx="2" />
    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
  </svg>
);

const ROLES = [
  { value: 'Reception', label: 'Reception', desc: 'Students & front desk' },
  { value: 'Instructor', label: 'Instructor', desc: 'Classes & attendance' },
  { value: 'Finance', label: 'Finance', desc: 'Payments & invoices' },
  { value: 'Admin', label: 'Admin', desc: 'Full workspace access' },
];

const Register = () => {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [requestedRole, setRequestedRole] = useState('Reception');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const passwordsMatch = confirmPassword.length > 0 && password === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!fullName || !email || !phone || !password || !confirmPassword) {
      setError('All fields are required.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    try {
      setLoading(true);
      const result = await userService.register({
        fullName,
        email,
        phone,
        password,
        requestedRole,
      });

      setSuccessMessage(
        result.message || 'Account created successfully. An administrator will review your request before you can sign in.'
      );

      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 3000);
    } catch (err) {
      setError(err?.error || err?.message || 'Registration failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <main className="login-page login-page--register">
      <aside className="login-showcase" aria-label="Fabritech Training Center">
        <div className="login-showcase__media">
          <img
            src="/student.png"
            alt="Student learning on a laptop"
            className="login-showcase__student"
          />
          <div className="login-showcase__overlay" aria-hidden="true" />
          <div className="login-showcase__pattern" aria-hidden="true" />
        </div>

        <div className="login-showcase__inner">
          <header className="login-showcase__brand">
            <img src="/logo.png" alt="Fabritech logo" className="login-showcase__logo" />
            <span>Training Center</span>
          </header>

          <div className="login-showcase__headline">
            <p className="login-showcase__eyebrow">Join the team</p>
            <h1>
              Request staff
              <strong> access.</strong>
            </h1>
            <span className="login-showcase__line" aria-hidden="true" />
            <p className="login-showcase__tagline">
              Create your profile and choose a role. An administrator reviews every request before access is granted.
            </p>
          </div>

          <ol className="login-steps" aria-label="How staff access works">
            <li className="login-step">
              <span>01</span>
              <div>
                <strong>Submit</strong>
                <small>Your profile</small>
              </div>
            </li>
            <li className="login-step">
              <span>02</span>
              <div>
                <strong>Review</strong>
                <small>Admin approval</small>
              </div>
            </li>
            <li className="login-step">
              <span>03</span>
              <div>
                <strong>Sign in</strong>
                <small>Once approved</small>
              </div>
            </li>
          </ol>
        </div>
      </aside>

      <section className="login-portal login-portal--signin" aria-label="Staff registration">
        <header className="login-portal__bar">
          <div className="login-portal__brand">
            <img src="/logo.png" alt="Fabritech logo" className="login-logo" />
            <div>
              <div className="login-brand-name">Fabritech</div>
              <div className="login-brand-tag">Training Center</div>
            </div>
          </div>
          <ThemeToggle className="theme-toggle--portal" showLabels />
        </header>

        <div className="login-portal__body">
          <div className="login-signin login-signin--register">
            <div className="login-card__heading">
              <span className="login-badge">New staff</span>
              <h2 className="login-title">Create account</h2>
              <p className="login-subtitle">
                Submit your details. An administrator will approve access before you can sign in.
              </p>
            </div>

            {error && (
              <div className="login-alert login-alert--error" role="alert">{error}</div>
            )}
            {successMessage && (
              <div className="login-alert login-alert--success" role="status">{successMessage}</div>
            )}

            <form className="login-form login-form--signin login-form--register" onSubmit={handleSubmit} noValidate>
              <div className="login-field">
                <label htmlFor="fullName">Full name</label>
                <div className="login-input-wrap">
                  <span className="login-input-icon"><UserIcon /></span>
                  <input
                    type="text"
                    id="fullName"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Jane Uwase"
                    disabled={loading}
                    autoComplete="name"
                    required
                  />
                </div>
              </div>

              <div className="login-field">
                <label htmlFor="email">Work email</label>
                <div className="login-input-wrap">
                  <span className="login-input-icon"><MailIcon /></span>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="nina.v@example.com"
                    disabled={loading}
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              <div className="login-field">
                <label htmlFor="phone">Phone number</label>
                <div className="login-input-wrap">
                  <span className="login-input-icon"><PhoneIcon /></span>
                  <input
                    type="tel"
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+250 7xx xxx xxx"
                    disabled={loading}
                    autoComplete="tel"
                    required
                  />
                </div>
              </div>

              <div className="login-field">
                <label htmlFor="requestedRole">Requested role</label>
                <div className="login-input-wrap">
                  <span className="login-input-icon"><BriefcaseIcon /></span>
                  <select
                    id="requestedRole"
                    className="login-select"
                    value={requestedRole}
                    onChange={(e) => setRequestedRole(e.target.value)}
                    disabled={loading}
                  >
                    {ROLES.map((role) => (
                      <option key={role.value} value={role.value} title={role.desc}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="login-field">
                <label htmlFor="password">Password</label>
                <div className="login-password-wrap">
                  <span className="login-input-icon"><LockIcon /></span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimum 6 characters"
                    disabled={loading}
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    disabled={loading}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              <div className="login-field">
                <div className="login-field__top">
                  <label htmlFor="confirmPassword">Confirm password</label>
                  {confirmPassword.length > 0 && (
                    <span className={`login-hint ${passwordsMatch ? 'is-ok' : 'is-warn'}`}>
                      {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
                    </span>
                  )}
                </div>
                <div className="login-password-wrap">
                  <span className="login-input-icon"><LockIcon /></span>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter password"
                    disabled={loading}
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    disabled={loading}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              <button type="submit" className="login-submit login-field--full" disabled={loading}>
                {loading ? 'Submitting request…' : 'Submit access request'}
                {loading ? <span className="login-spinner" aria-hidden="true" /> : <span className="login-submit__icon"><ArrowIcon /></span>}
              </button>
            </form>

            <div className="login-alt">
              <p>
                Already have an account? <Link to="/login">Sign in</Link>
              </p>
            </div>
          </div>
        </div>

        <footer className="login-portal__foot">
          <span><ShieldIcon /> Admin review required</span>
          <a href="mailto:info@fabritech.rw">Need help?</a>
        </footer>
      </section>
    </main>
  );
};

export default Register;
